import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import PaymentServices from "../../services/externalServices/TicketServices/paymentServices";
import ScreenLayoutServices from "../../services/externalServices/TicketServices/screenLayoutServices";
import moment from "moment";

export default function PaymentStatusV2() {
  const { id } = useParams(); // merchantOrderId
  const location = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState("checking");
  const [error, setError] = useState("");

  const stateV2 = JSON.parse(sessionStorage.getItem("tempV2Booking") || 'null');
  const stateLegacyUnlimit = JSON.parse(sessionStorage.getItem("tempUnlimitBooking") || 'null');
  const merged = location.state || stateV2 || stateLegacyUnlimit || {};
  const {
    showtimeId,
    theatreId,
    bookedSeats,
    date,
    time,
    totalPrice,
    movie,
    nameOfTheatre,
    phoneNumber,
    priceSplit,
    bookingId,
    maxTimeLimit,
    code,
    isSectionLayout
  } = merged;

  useEffect(() => {
    const poll = async (attempt = 0, maxAttempts = 12) => {
      try {
        if (!bookingId) {
          setStatus("failed");
          setError("Missing booking reference.");
          return;
        }
        const res = await PaymentServices.validateBooking(bookingId);
        const st = res?.booking?.status;
        if (st === "CONFIRMED") {
          navigate("/paymentTicketV2", {
            state: {
              movie,
              theatreId,
              showtimeId,
              bookedSeats,
              date,
              time,
              totalPrice,
              taxes: (Number(totalPrice) * 0.18),
              ticketTotal: (Number(totalPrice) * 1.18),
              transactionId: id,
              transactionStatus: "Success",
              nameOfTheatre,
              bookingId,
              previousPage: "/movies",
              phoneNumber,
              sendNoWhatsApp: true,
              maxTimeLimit,
              code,
              isSectionLayout
            },
            replace: true
          });
          return;
        }
        if (["FAILED_PAYMENT","CANCELLED","DUPLICATE"].includes(st)) {
          setStatus("failed");
          setError("Booking was cancelled or payment failed.");
          return;
        }
        if (attempt < maxAttempts - 1) {
          setTimeout(() => poll(attempt + 1, maxAttempts), 4000);
        } else {
          // timeout
          setStatus("failed");
          setError("Payment confirmation not received. If amount was deducted, it may be refunded automatically.");
        }
      } catch (e) {
        setStatus("failed");
        setError("Error checking booking status.");
      }
    };
    poll();
  }, [id, bookingId]);

  const handleReleaseSeats = async () => {
    try {
      if (!theatreId || !showtimeId || !Array.isArray(bookedSeats) || !date || !time) {
        navigate("/movies", { replace: true });
        return;
      }
      const payload = {
        showtimeId,
        theatreId,
        cancelledSeats: bookedSeats,
        date: moment(date).format('YYYY/MM/DD'),
        time,
        status: "FAILED_PAYMENT",
        bookingId: bookingId
      };
      await ScreenLayoutServices.cancelBooking(payload);
      try {
        const layoutReq = { theatreId, date, time, showtimeId };
        const seatLayout = await ScreenLayoutServices.getBookedScreenLayout(layoutReq);
        if (seatLayout?.seatLayout) {
          navigate("/bookedSeats", {
            state: {
              seatLayout,
              date,
              time,
              theatreId,
              showtimeId,
              movie,
              nameOfTheatre,
              maxTimeLimit,
              code,
              isSectionLayout
            },
            replace: true
          });
          return;
        }
      } catch (e) {
        // fall through to default navigation
      }
      navigate("/bookedSeats", {
        state: {
          date,
          time,
          theatreId,
          showtimeId,
          movie,
          nameOfTheatre,
          maxTimeLimit,
          code,
          isSectionLayout
        },
        replace: true
      });
    } catch (e) {
      navigate("/bookedSeats", {
        state: {
          date,
          time,
          theatreId,
          showtimeId,
          movie,
          nameOfTheatre,
          maxTimeLimit,
          code,
          isSectionLayout
        },
        replace: true
      });
    }
  };

  return (
    <div className="max-w-xl mx-auto mt-20 p-6 rounded-xl shadow-lg text-center bg-white">
      {status === "checking" && (
        <>
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Processing your payment...</h2>
          <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto"></div>
        </>
      )}
      {status === "failed" && (
        <>
          <h2 className="text-2xl font-semibold text-red-600 mb-2">❌ Payment Failed</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <button
              className="px-4 py-2 bg-black text-white rounded"
              onClick={handleReleaseSeats}
            >
              Release Seats & Go Back
            </button>
            <button
              className="px-4 py-2 bg-gray-200 rounded"
              onClick={() => navigate("/moviebooking", { replace: true })}
            >
              Back to Movies
            </button>
          </div>
        </>
      )}
    </div>
  );
}
